import React, { useState } from 'react';
import { Card, Table, Badge, Button } from 'react-bootstrap';
import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';

export type SortDirection = 'asc' | 'desc' | null;

export interface ColumnDef<T> {
    key: string;
    header: string;
    sortable?: boolean;
    sortKey?: string; // If different from display key
    width?: string;
    align?: 'left' | 'center' | 'right';
    render?: (row: T) => React.ReactNode;
    cellStyle?: React.CSSProperties;
}

export interface ActionButton<T> {
    label: string;
    icon?: React.ReactNode;
    variant?: 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info';
    onClick: (row: T) => void;
    show?: (row: T) => boolean;
    style?: React.CSSProperties;
}

interface DataTableProps<T> {
    columns: ColumnDef<T>[];
    data: T[];
    actions?: ActionButton<T>[];
    onRowClick?: (row: T) => void;
    emptyMessage?: string;
    keyExtractor: (row: T) => string;
    rowHoverEffect?: boolean;
}

export function DataTable<T>({
    columns,
    data,
    actions,
    onRowClick,
    emptyMessage = 'No data found',
    keyExtractor,
    rowHoverEffect = true,
}: DataTableProps<T>) {
    const [sortField, setSortField] = useState<string | null>(null);
    const [sortDirection, setSortDirection] = useState<SortDirection>(null);

    const handleSort = (column: ColumnDef<T>) => {
        if (!column.sortable) return;

        const field = column.sortKey || column.key;

        if (sortField === field) {
            // Cycle through: asc -> desc -> null
            if (sortDirection === 'asc') {
                setSortDirection('desc');
            } else if (sortDirection === 'desc') {
                setSortField(null);
                setSortDirection(null);
            }
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const getSortIcon = (column: ColumnDef<T>) => {
        if (!column.sortable) return null;

        const field = column.sortKey || column.key;

        if (sortField !== field) {
            return <ArrowUpDown size={14} style={{ opacity: 0.4, marginLeft: '0.25rem' }} />;
        }
        if (sortDirection === 'asc') {
            return <ArrowUp size={14} style={{ marginLeft: '0.25rem' }} />;
        }
        return <ArrowDown size={14} style={{ marginLeft: '0.25rem' }} />;
    };

    const sortedData = React.useMemo(() => {
        if (!sortField || !sortDirection) return data;

        return [...data].sort((a, b) => {
            const aValue = (a as any)[sortField];
            const bValue = (b as any)[sortField];

            if (sortDirection === 'asc') {
                return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
            } else {
                return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
            }
        });
    }, [data, sortField, sortDirection]);

    const getActionButtonStyle = (variant?: string): React.CSSProperties => {
        const baseStyle: React.CSSProperties = {
            border: 'none',
            borderRadius: '6px',
            padding: '0.375rem 0.75rem',
            fontSize: '0.75rem',
            fontWeight: '500',
        };

        switch (variant) {
            case 'primary':
                return { ...baseStyle, backgroundColor: '#3b82f6', color: '#ffffff' };
            case 'success':
                return { ...baseStyle, backgroundColor: '#10b981', color: '#ffffff' };
            case 'danger':
                return { ...baseStyle, backgroundColor: '#fee2e2', color: '#ef4444' };
            case 'warning':
                return { ...baseStyle, backgroundColor: '#fef3c7', color: '#f59e0b' };
            case 'info':
                return { ...baseStyle, backgroundColor: '#dbeafe', color: '#3b82f6' };
            case 'secondary':
            default:
                return { ...baseStyle, backgroundColor: '#f3f4f6', color: '#374151' };
        }
    };

    return (
        <Card className="border-0 shadow-sm">
            <Card.Body className="p-0">
                <div style={{ overflowX: 'auto' }}>
                    <Table className="mb-0" style={{ minWidth: '900px' }}>
                        <thead>
                            <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                                {columns.map((column) => (
                                    <th
                                        key={column.key}
                                        onClick={() => handleSort(column)}
                                        style={{
                                            padding: '1rem 1.25rem',
                                            fontSize: '0.75rem',
                                            fontWeight: '600',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em',
                                            color: '#6b7280',
                                            backgroundColor: '#f9fafb',
                                            cursor: column.sortable ? 'pointer' : 'default',
                                            userSelect: 'none',
                                            transition: 'background-color 0.15s ease',
                                            width: column.width,
                                            textAlign: column.align || 'left',
                                        }}
                                        onMouseEnter={(e) => {
                                            if (column.sortable) {
                                                e.currentTarget.style.backgroundColor = '#f3f4f6';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = '#f9fafb';
                                        }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: column.align === 'right' ? 'flex-end' : column.align === 'center' ? 'center' : 'flex-start' }}>
                                            {column.header}
                                            {getSortIcon(column)}
                                        </div>
                                    </th>
                                ))}
                                {actions && actions.length > 0 && (
                                    <th
                                        style={{
                                            padding: '1rem 1.25rem',
                                            fontSize: '0.75rem',
                                            fontWeight: '600',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em',
                                            color: '#6b7280',
                                            backgroundColor: '#f9fafb',
                                            textAlign: 'right',
                                        }}
                                    >
                                        Actions
                                    </th>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {sortedData.map((row) => (
                                <tr
                                    key={keyExtractor(row)}
                                    onClick={() => onRowClick?.(row)}
                                    style={{
                                        cursor: onRowClick ? 'pointer' : 'default',
                                        borderBottom: '1px solid #f3f4f6',
                                        transition: rowHoverEffect ? 'background-color 0.15s ease' : 'none',
                                    }}
                                    onMouseEnter={(e) => {
                                        if (rowHoverEffect) {
                                            e.currentTarget.style.backgroundColor = '#f9fafb';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (rowHoverEffect) {
                                            e.currentTarget.style.backgroundColor = 'transparent';
                                        }
                                    }}
                                >
                                    {columns.map((column) => (
                                        <td
                                            key={column.key}
                                            style={{
                                                padding: '1rem 1.25rem',
                                                textAlign: column.align || 'left',
                                                ...column.cellStyle,
                                            }}
                                        >
                                            {column.render ? column.render(row) : (row as any)[column.key]}
                                        </td>
                                    ))}
                                    {actions && actions.length > 0 && (
                                        <td
                                            style={{ padding: '1rem 1.25rem', textAlign: 'right' }}
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <div className="d-flex gap-2 justify-content-end">
                                                {actions.map((action, idx) => {
                                                    const shouldShow = action.show ? action.show(row) : true;
                                                    if (!shouldShow) return null;

                                                    return (
                                                        <Button
                                                            key={idx}
                                                            size="sm"
                                                            onClick={() => action.onClick(row)}
                                                            style={{
                                                                ...getActionButtonStyle(action.variant),
                                                                ...action.style,
                                                            }}
                                                        >
                                                            {action.icon && <span className="me-1">{action.icon}</span>}
                                                            {action.label}
                                                        </Button>
                                                    );
                                                })}
                                            </div>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
                {sortedData.length === 0 && (
                    <div className="text-center py-5" style={{ color: '#6b7280' }}>
                        {emptyMessage}
                    </div>
                )}
            </Card.Body>
        </Card>
    );
}

// Helper to create status badges
export const StatusBadge: React.FC<{
    status: string;
    variant?: 'success' | 'danger' | 'warning' | 'info' | 'secondary';
}> = ({ status, variant = 'secondary' }) => {
    const getConfig = () => {
        switch (variant) {
            case 'success':
                return { bg: '#10b981', color: '#ffffff' };
            case 'danger':
                return { bg: '#ef4444', color: '#ffffff' };
            case 'warning':
                return { bg: '#f59e0b', color: '#ffffff' };
            case 'info':
                return { bg: '#3b82f6', color: '#ffffff' };
            default:
                return { bg: '#6b7280', color: '#ffffff' };
        }
    };

    const config = getConfig();

    return (
        <Badge
            style={{
                backgroundColor: config.bg,
                color: config.color,
                padding: '0.375rem 0.75rem',
                fontSize: '0.75rem',
                fontWeight: '500',
                borderRadius: '6px',
                border: 'none',
            }}
        >
            {status}
        </Badge>
    );
};

export default DataTable;